import { useEffect } from 'react';
import PropTypes from 'prop-types';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
import { APP_MODULES } from 'src/constants/app-modules';
import { useOrganizationContext } from 'src/context/organization';
import { useAuthContext } from '../hooks';

// ----------------------------------------------------------------------

export default function CampaignFinancesGuard({ children }) {
  const router = useRouter();
  const { user } = useAuthContext();
  const { selectedOrganizationId } = useOrganizationContext();

  // Wait for user and selectedOrganizationId to be loaded
  const isLoading = !user || !selectedOrganizationId;

  const userRole = user?.users_user_roles?.length
    ? user?.users_user_roles[0].user_roles_roles?.code
    : null;

  // Check if user has campaign finances permission for the selected organization
  const hasCampaignFinancesPermission = user?.users_module_permissions?.some(
    (permission) =>
      permission?.organization_id === selectedOrganizationId &&
      permission?.user_module_permissions_app_module?.module === APP_MODULES.CAMPAIGN_FINANCES
  );

  useEffect(() => {
    if (isLoading) return; // Don't run logic until data is loaded

    // Superadmin always has access
    if (userRole === 'superadmin') {
      return;
    }

    // If user doesn't have permission, redirect to dashboard
    if (!!userRole && !hasCampaignFinancesPermission) {
      router.replace(paths.dashboard.campaigns);
    }
  }, [userRole, hasCampaignFinancesPermission, router, selectedOrganizationId, isLoading]);

  // Superadmin always has access
  if (userRole === 'superadmin') {
    return <>{children}</>;
  }

  // If user doesn't have permission, don't render children (will redirect)
  if (!hasCampaignFinancesPermission || isLoading) {
    return null;
  }

  return <>{children}</>;
}

CampaignFinancesGuard.propTypes = {
  children: PropTypes.node,
};
